// ============================================
// White Balance Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { WHITE_BALANCE_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * White Balance post-processing effect
 * Adjusts color temperature and tint
 */
export class WhiteBalanceEffect extends PostProcessEffect {
  readonly type = 'whiteBalance';
  readonly displayName = 'White Balance';
  
  // Parameters
  temperature = 0.0; // -100 (cool) to 100 (warm)
  tint = 0.0;        // -100 (green) to 100 (magenta)
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = WHITE_BALANCE_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'temperature', type: 'float', min: -100, max: 100, default: 0.0, description: 'Color temperature (-100 cool, +100 warm)' },
      { name: 'tint', type: 'float', min: -100, max: 100, default: 0.0, description: 'Green/Magenta tint' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'temperature': return this.temperature;
      case 'tint': return this.tint;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'temperature': this.temperature = value; break;
      case 'tint': this.tint = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const whiteBalanceWgsl = getCompiledShader('postprocess/white-balance');
    this.shader = ctx.createShader(whiteBalanceWgsl, whiteBalanceWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'white-balance-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'white-balance-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;
    
    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      temperature: this.temperature * intensity,
      tint: this.tint * intensity,
      _pad: [0, 0],
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
